import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { AdminAuditTrace } from "./AdminAuditTrace";
import "./AdminSecurityControlCenter.css";

type ReviewStatus="OPEN"|"ACKNOWLEDGED"|"RESOLVED"|"IGNORED";
type SecurityView="OVERVIEW"|"FINDINGS"|"AUTH"|"RISK"|"TRACE";

type Check={key:string;label:string;status:string;detail:string};
type Finding={
  findingKey:string;severity:string;domain:string;title:string;detail:string;
  organizationId?:string|null;organizationName?:string|null;
  resourceType:string;resourceId:string;occurredAt:string;correlationId?:string|null;
  suggestedRoute?:string|null;metadata?:Record<string,unknown>;
  reviewStatus:ReviewStatus;reviewNote?:string|null;reviewedAt?:string|null;
};
type RiskAction={
  auditId:string;occurredAt:string;correlationId?:string|null;actionKey:string;
  resourceType:string;resourceId?:string|null;result:string;actorName?:string|null;organizationName?:string|null;
};
type AuthEvent={auditId:string;createdAt:string;action?:string|null;ipAddress?:string|null;subjectId?:string|null};
type ControlCenter={
  windowHours:number;
  summary:{open:number;acknowledged:number;resolved:number;ignored:number;highOpen:number;attentionOpen:number;riskActions:number;authEvents:number};
  checks:Check[];
  findings:Finding[];
  riskActions:RiskAction[];
  authEvents:AuthEvent[];
};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
function statusLabel(value:string){
  const labels:Record<string,string>={OPEN:"미처리",ACKNOWLEDGED:"확인 중",RESOLVED:"해결",IGNORED:"예외 처리"};
  return labels[value] || value;
}
function domainLabel(value:string){
  const labels:Record<string,string>={IDENTITY:"계정",ACCESS:"권한",CREDENTIAL:"발급",DELIVERY:"전달",CLAIM:"수령",AUDIT:"감사"};
  return labels[value] || value;
}

export function AdminSecurityControlCenter({client}:{client:PlatformClient}){
  const [view,setView]=useState<SecurityView>("OVERVIEW");
  const [data,setData]=useState<ControlCenter|null>(null);
  const [windowHours,setWindowHours]=useState(24);
  const [selectedKey,setSelectedKey]=useState("");
  const [traceCorrelation,setTraceCorrelation]=useState("");
  const [statusFilter,setStatusFilter]=useState<"ALL"|ReviewStatus>("OPEN");
  const [severityFilter,setSeverityFilter]=useState("ALL");
  const [domainFilter,setDomainFilter]=useState("ALL");
  const [query,setQuery]=useState("");
  const [reviewNote,setReviewNote]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_security_control_center",{p_window_hours:windowHours}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as ControlCenter;
      setData(next);
      setSelectedKey((current)=>
        next.findings.some((item)=>item.findingKey===current)
          ? current
          : next.findings.find((item)=>item.reviewStatus==="OPEN")?.findingKey || next.findings[0]?.findingKey || ""
      );
    });
    return()=>{active=false;};
  },[client,windowHours,refresh]);

  const selected=useMemo(()=>data?.findings.find((item)=>item.findingKey===selectedKey) || null,[data,selectedKey]);
  useEffect(()=>{setReviewNote(selected?.reviewNote || "");},[selected?.findingKey]);

  const domains=useMemo(()=>["ALL",...Array.from(new Set((data?.findings || []).map((item)=>item.domain)))],[data]);
  const visibleFindings=useMemo(()=>{
    const keyword=query.trim().toLowerCase();
    return (data?.findings || []).filter((item)=>{
      if(statusFilter!=="ALL" && item.reviewStatus!==statusFilter)return false;
      if(severityFilter!=="ALL" && item.severity!==severityFilter)return false;
      if(domainFilter!=="ALL" && item.domain!==domainFilter)return false;
      return !keyword || [item.title,item.detail,item.organizationName || "",item.resourceType,item.resourceId]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[data,statusFilter,severityFilter,domainFilter,query]);

  async function review(status:Exclude<ReviewStatus,"OPEN">){
    if(!selected)return;
    setBusy(status);setError("");setMessage("");
    const {error}=await client.rpc("admin_review_security_finding",{
      p_finding_key:selected.findingKey,
      p_status:status,
      p_note:reviewNote.trim() || null,
      p_source_observed_at:selected.occurredAt
    });
    setBusy("");
    if(error){setError(error.message);return;}
    setMessage(status==="RESOLVED"?"Finding을 해결 처리했습니다.":status==="IGNORED"?"Finding을 예외 처리했습니다.":"Finding을 확인 중으로 표시했습니다.");
    setRefresh((value)=>value+1);
  }

  function openTrace(correlationId?:string|null){
    if(correlationId)setTraceCorrelation(correlationId);
    setView("TRACE");
  }

  if(!data)return <p className="muted">보안·감사 관제 정보를 불러오는 중…</p>;

  const totalAction=data.summary.highOpen+data.summary.attentionOpen;
  const overall=data.summary.highOpen>0?"BLOCKED":data.summary.open>0?"ATTENTION":"PASS";

  return (
    <section className="security-control-page">
      <header className="security-control-hero">
        <div>
          <p className="eyebrow">PLATFORM SECURITY & AUDIT CONTROL CENTER</p>
          <h2>보안·감사 관제센터</h2>
          <p>계정·권한·초대·발급·이메일·Wallet Claim·Audit 실패를 한 곳에서 탐지하고 관련 Correlation까지 추적합니다.</p>
        </div>
        <div className={"security-overall "+overall.toLowerCase()}>
          <span>SECURITY POSTURE</span>
          <strong>{overall==="PASS"?"정상":overall==="ATTENTION"?"확인 필요":"우선 조치"}</strong>
          <small>{totalAction}개 미처리 Finding</small>
        </div>
      </header>

      <div className="security-kpis">
        <SecurityKpi label="High 미처리" value={data.summary.highOpen} tone={data.summary.highOpen>0?"danger":"normal"} />
        <SecurityKpi label="Attention 미처리" value={data.summary.attentionOpen} tone={data.summary.attentionOpen>0?"warn":"normal"} />
        <SecurityKpi label="확인 중" value={data.summary.acknowledged} />
        <SecurityKpi label="민감 작업" value={data.summary.riskActions} />
        <SecurityKpi label="Auth Audit" value={data.summary.authEvents} />
      </div>

      <nav className="security-tabs">
        <button className={view==="OVERVIEW"?"active":""} onClick={()=>setView("OVERVIEW")}>관제 홈</button>
        <button className={view==="FINDINGS"?"active":""} onClick={()=>setView("FINDINGS")}>Finding {data.summary.open || ""}</button>
        <button className={view==="RISK"?"active":""} onClick={()=>setView("RISK")}>민감 작업</button>
        <button className={view==="AUTH"?"active":""} onClick={()=>setView("AUTH")}>인증 로그</button>
        <button className={view==="TRACE"?"active":""} onClick={()=>setView("TRACE")}>Audit Trace</button>
      </nav>

      <div className="security-window">
        <span>관찰 범위</span>
        {[24,72,168,720].map((hours)=>(
          <button key={hours} className={windowHours===hours?"active":""} onClick={()=>setWindowHours(hours)}>
            {hours===24?"24시간":hours===72?"3일":hours===168?"7일":"30일"}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      {view==="OVERVIEW" && <SecurityOverview data={data} onOpenFindings={()=>setView("FINDINGS")} onOpenTrace={openTrace} />}

      {view==="FINDINGS" && (
        <div className="security-findings-view">
          <div className="security-filterbar">
            <input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="Finding·기관·리소스 검색" />
            <select value={statusFilter} onChange={(event)=>setStatusFilter(event.target.value as "ALL"|ReviewStatus)}>
              <option value="ALL">전체 상태</option><option value="OPEN">미처리</option><option value="ACKNOWLEDGED">확인 중</option>
              <option value="RESOLVED">해결</option><option value="IGNORED">예외 처리</option>
            </select>
            <select value={severityFilter} onChange={(event)=>setSeverityFilter(event.target.value)}>
              <option value="ALL">전체 심각도</option><option value="HIGH">High</option><option value="ATTENTION">Attention</option>
            </select>
            <select value={domainFilter} onChange={(event)=>setDomainFilter(event.target.value)}>
              {domains.map((item)=><option key={item} value={item}>{item==="ALL"?"전체 영역":domainLabel(item)}</option>)}
            </select>
          </div>

          <div className="security-finding-layout">
            <aside className="security-finding-list">
              <div className="panel-title"><strong>Security Finding</strong><span>{visibleFindings.length}건</span></div>
              {visibleFindings.map((item)=>(
                <button key={item.findingKey} className={selectedKey===item.findingKey?"active":""} onClick={()=>setSelectedKey(item.findingKey)}>
                  <div>
                    <span className={"security-severity "+item.severity.toLowerCase()}>{item.severity}</span>
                    <span className={"security-review-status "+item.reviewStatus.toLowerCase()}>{statusLabel(item.reviewStatus)}</span>
                  </div>
                  <strong>{item.title}</strong>
                  <p>{item.organizationName || domainLabel(item.domain)}</p>
                  <small>{formatDate(item.occurredAt)}</small>
                </button>
              ))}
              {!visibleFindings.length && <p className="case-empty">조건에 맞는 Finding이 없습니다.</p>}
            </aside>

            <main className="security-finding-detail">
              {selected ? (
                <>
                  <header>
                    <div>
                      <div className="security-detail-tags">
                        <span className={"security-severity "+selected.severity.toLowerCase()}>{selected.severity}</span>
                        <span>{domainLabel(selected.domain)}</span><span>{statusLabel(selected.reviewStatus)}</span>
                      </div>
                      <h2>{selected.title}</h2><p>{selected.detail}</p>
                    </div>
                  </header>

                  <div className="security-resource-grid">
                    <SecurityInfo label="기관" value={selected.organizationName || "-"} />
                    <SecurityInfo label="Resource" value={selected.resourceType} />
                    <SecurityInfo label="Resource ID" value={selected.resourceId} />
                    <SecurityInfo label="관찰 시각" value={formatDate(selected.occurredAt)} />
                  </div>

                  {selected.metadata && Object.keys(selected.metadata).length>0 && (
                    <section className="security-detail-card">
                      <div className="panel-title"><strong>탐지 근거</strong><span>Metadata</span></div>
                      <div className="security-metadata">
                        {Object.entries(selected.metadata).map(([key,value])=>(
                          <div key={key}><span>{key}</span><strong>{String(value ?? "-")}</strong></div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="security-detail-card">
                    <div className="panel-title"><strong>관리자 검토</strong><span>{selected.reviewedAt?formatDate(selected.reviewedAt):"미검토"}</span></div>
                    <textarea rows={4} value={reviewNote} onChange={(event)=>setReviewNote(event.target.value)} placeholder="확인 내용, 조치 결과, 예외 사유를 기록하세요." />
                    <div className="security-review-actions">
                      {selected.suggestedRoute && <button className="secondary" onClick={()=>navigate(selected.suggestedRoute || "/audit")}>관련 화면</button>}
                      {selected.correlationId && <button className="secondary" onClick={()=>openTrace(selected.correlationId)}>Correlation 추적</button>}
                      <span />
                      <button className="secondary" disabled={busy!==""} onClick={()=>review("IGNORED")}>예외</button>
                      <button className="secondary" disabled={busy!==""} onClick={()=>review("ACKNOWLEDGED")}>확인 중</button>
                      <button className="primary" disabled={busy!==""} onClick={()=>review("RESOLVED")}>해결 처리</button>
                    </div>
                  </section>
                </>
              ):<p className="case-empty">Finding을 선택하세요.</p>}
            </main>
          </div>
        </div>
      )}

      {view==="RISK" && (
        <section className="security-table-card">
          <div className="panel-title"><strong>최근 민감 작업</strong><span>{data.riskActions.length}건</span></div>
          <div className="security-risk-table">
            <div className="security-risk-row head"><span>시각</span><span>작업</span><span>대상</span><span>Actor</span><span>기관</span><span>결과</span><span>추적</span></div>
            {data.riskActions.map((item)=>(
              <div className="security-risk-row" key={item.auditId}>
                <span>{formatDate(item.occurredAt)}</span><strong>{item.actionKey}</strong>
                <span>{item.resourceType}{item.resourceId?" · "+item.resourceId:""}</span>
                <span>{item.actorName || "-"}</span><span>{item.organizationName || "-"}</span>
                <span className={item.result==="SUCCESS"?"security-result good":"security-result bad"}>{item.result}</span>
                <button disabled={!item.correlationId} onClick={()=>openTrace(item.correlationId)}>Trace</button>
              </div>
            ))}
            {!data.riskActions.length && <p className="case-empty">해당 기간의 민감 작업이 없습니다.</p>}
          </div>
        </section>
      )}

      {view==="AUTH" && (
        <section className="security-table-card">
          <div className="panel-title"><strong>Supabase Auth Audit</strong><span>{data.authEvents.length}건</span></div>
          {data.authEvents.length ? (
            <div className="security-auth-list">
              {data.authEvents.map((item)=>(
                <div key={item.auditId}>
                  <div><strong>{item.action || "Auth event"}</strong><span>{formatDate(item.createdAt)}</span></div>
                  <small>IP {item.ipAddress || "-"} · Subject {item.subjectId || "-"}</small>
                </div>
              ))}
            </div>
          ):(
            <div className="security-auth-empty">
              <strong>현재 관찰 범위에 DB 저장 Auth Audit 이벤트가 없습니다.</strong>
              <p>인증 로그 DB 저장 설정이 꺼져 있거나 해당 기간에 인증 이벤트가 없을 수 있습니다. RS 자체 Audit은 별도로 계속 기록됩니다.</p>
            </div>
          )}
        </section>
      )}

      {view==="TRACE" && <AdminAuditTrace client={client} initialCorrelationId={traceCorrelation || undefined} />}
    </section>
  );
}

function SecurityOverview({data,onOpenFindings,onOpenTrace}:{data:ControlCenter;onOpenFindings():void;onOpenTrace(id?:string|null):void}){
  const open=data.findings.filter((item)=>item.reviewStatus==="OPEN");
  const domainCounts=Array.from(new Set(open.map((item)=>item.domain))).map((domain)=>({domain,count:open.filter((item)=>item.domain===domain).length}));

  return (
    <div className="security-overview">
      <section className="security-check-card">
        <div className="panel-title"><strong>핵심 보안 체크</strong><span>{data.checks.length} checks</span></div>
        <div className="security-check-list">
          {data.checks.map((item)=>(
            <div key={item.key}>
              <span className={"security-check-state "+item.status.toLowerCase()}>{item.status==="PASS"?"정상":item.status==="BLOCKED"?"조치":"확인"}</span>
              <div><strong>{item.label}</strong><small>{item.detail}</small></div>
            </div>
          ))}
        </div>
      </section>

      <section className="security-check-card">
        <div className="panel-title"><strong>영역별 미처리 Finding</strong><button className="text-button" onClick={onOpenFindings}>전체 보기 →</button></div>
        <div className="security-domain-grid">
          {domainCounts.map((item)=><div key={item.domain}><span>{domainLabel(item.domain)}</span><strong>{item.count}</strong></div>)}
          {!domainCounts.length && <div className="security-clear-state"><strong>미처리 Finding 없음</strong><span>현재 탐지 규칙 기준 정상입니다.</span></div>}
        </div>
      </section>

      <section className="security-table-card security-recent-risk">
        <div className="panel-title"><strong>최근 민감 작업</strong><span>{data.riskActions.length}건</span></div>
        {data.riskActions.slice(0,8).map((item)=>(
          <div className="security-recent-row" key={item.auditId}>
            <div><strong>{item.actionKey}</strong><small>{item.actorName || "-"} · {item.organizationName || "-"}</small></div>
            <span>{formatDate(item.occurredAt)}</span>
            <button disabled={!item.correlationId} onClick={()=>onOpenTrace(item.correlationId)}>Trace</button>
          </div>
        ))}
        {!data.riskActions.length && <p className="case-empty">해당 기간의 민감 작업이 없습니다.</p>}
      </section>
    </div>
  );
}

function SecurityKpi({label,value,tone="normal"}:{label:string;value:number;tone?:"normal"|"warn"|"danger"}){
  return <div className={"security-kpi "+tone}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}
function SecurityInfo({label,value}:{label:string;value:string}){
  return <div className="security-info"><span>{label}</span><strong>{value}</strong></div>;
}
